import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Header } from '@/components/layout/header/Header';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { authApi } from '@/services/authService';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronLeft,
  Mail,
  Hash,
  Wallet,
  TrendingUp,
  Receipt,
  Activity,
  Upload as UploadIcon,
  Eye,
  Download,
  ExternalLink,
  X,
  ZoomIn,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface PaymentData {
  _id: string;
  id_user: string;
  email: string;
  id_stripe_account: string;
  amount: number;
  totalAmount: number;
  totalTaxe: number;
  status: 'requested' | 'pending' | 'paid' | 'failed' | 'canceled';
  id_payout: string;
  createdAt: string;
  updatedAt: string;
  document?: string;
  obs?: string;
}

type PayoutStatus = 'requested' | 'pending' | 'paid' | 'failed' | 'canceled';

const statusStyles: Record<PayoutStatus, { cls: string; dot: string; label: string }> = {
  requested: {
    cls: 'bg-amber-50 text-amber-700 border-amber-100',
    dot: 'bg-amber-500',
    get label() { return translateInterfaceText("Solicitado"); },
  },
  pending: {
    cls: 'bg-orange-50 text-orange-700 border-orange-100',
    dot: 'bg-orange-500',
    get label() { return translateInterfaceText("Pendente"); },
  },
  paid: {
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    dot: 'bg-emerald-500',
    get label() { return translateInterfaceText("Pago"); },
  },
  failed: {
    cls: 'bg-red-50 text-red-700 border-red-100',
    dot: 'bg-red-500',
    get label() { return translateInterfaceText("Falhou"); },
  },
  canceled: {
    cls: 'bg-gray-100 text-gray-600 border-gray-200',
    dot: 'bg-gray-400',
    get label() { return translateInterfaceText("Cancelado"); },
  },
};

const formatBRL = (v: number | undefined) =>
  (typeof v === 'number' ? v : 0).toLocaleString(getInterfaceLanguage(), {
    style: 'currency',
    currency: 'BRL',
  });

interface MiniCardProps {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}
const MiniCard: React.FC<MiniCardProps> = ({ icon: Icon, label, value }) => { useLanguage(); return ((
  <div className="p-4 rounded-2xl border border-slate-200 bg-white">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[12px] font-medium text-[#6B7280]">{label}</p>
    </div>
    <div className="text-[14.5px] font-semibold text-[#0A1A2F] break-all">{value}</div>
  </div>
)); };

export default function UpdatePayment() {
  useLanguage();
  const { id_payout } = useParams<{ id_payout: string }>();
  const { user } = useCustomAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [payment, setPayment] = useState<PaymentData | null>(null);
  const [newStatus, setNewStatus] = useState<PayoutStatus>('requested');
  const [documentLink, setDocumentLink] = useState<string>('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [note, setNote] = useState<string>('');

  const [isReceiptOpen, setIsReceiptOpen] = useState<boolean>(false);
  const [zoom, setZoom] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Guard de admin
  useEffect(() => {
    if (user && user.nivel !== 'admin') {
      toast.error(translateInterfaceText("Área restrita exclusivamente para administradores."));
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchPaymentInfo = useCallback(async () => {
    if (!id_payout) {
      toast.error(translateInterfaceText("ID do pagamento não fornecido."));
      navigate('/dashboard');
      return;
    }

    try {
      setLoading(true);
      const response = await authApi.get(
        `/stripe/afiliate/finance/admin/payment-informations/${id_payout}`,
      );
      if (response) {
        const data = response.data;
        setPayment(data);
        setNewStatus(data.status);
        setDocumentLink(data.document || '');
        setNote(data.obs || '');
      }
    } catch (err: any) {
      toast.error(translateInterfaceText("Erro ao buscar informações do pagamento."));
      console.log(err);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id_payout, navigate]);

  useEffect(() => {
    if (user?.nivel === 'admin') {
      fetchPaymentInfo();
    }
  }, [fetchPaymentInfo, user]);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error(translateInterfaceText("Envie uma imagem válida do comprovante."));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(translateInterfaceText("O arquivo precisa ter até 10MB."));
      return;
    }
    setProofFile(file);
    const url = URL.createObjectURL(file);
    setPreview(url);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };
  const handleSubmit = async () => {
    if (!payment) {
      toast.error(translateInterfaceText("Dados do pagamento não encontrados."));
      return;
    }

    if (!proofFile && newStatus === 'paid' && !documentLink) {
      toast.error(translateInterfaceText("Por favor, anexe o comprovante de transferência."));
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('status', newStatus);
      formData.append('id_payout', payment.id_payout);
      formData.append('obs', note)
      if (proofFile) formData.append('proof', proofFile);

      const response = await authApi.post(
        `/stripe/afiliate/finance/admin/payment-update`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      if (response.data && response.data.success) {
        toast.success(translateInterfaceText("Pagamento atualizado com sucesso!"));
        window.location.reload();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || translateInterfaceText("Erro ao atualizar pagamento."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user.nivel !== 'admin') return null;

  if (loading) {
    return (
      <Layout fixedSidebar>
        <div className="flex-1 flex items-center justify-center bg-white">
          <div className="animate-pulse flex flex-col items-center gap-3">
            <div className="h-8 w-8 rounded-full border-4 border-[#2563EB] border-t-transparent animate-spin" />
            <p className="text-xs font-medium text-[#6B7280] tracking-wide">
              {translateInterfaceText("Carregando dados do pagamento...")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const currentStatusKey = (payment?.status || 'requested') as PayoutStatus;
  const hasReceipt = !!preview || !!documentLink;
  const receiptSrc = preview || documentLink;

  return (
    <Layout fixedSidebar>
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        <div className="[&_.desktop-header]:!border-b-[#D1D5DB]">
          <Header title={translateInterfaceText("Atualização de Pagamento")} />
        </div>

        <main className="flex-1 overflow-y-auto scrollbar-blue">
          <div className="max-w-5xl mx-auto p-6 md:p-8 space-y-6">
            {/* Breadcrumb */}
            <button
              onClick={() => navigate('/AdminSummary')}
              className="inline-flex items-center gap-1.5 text-[12.5px] text-[#6B7280] hover:text-[#2563EB] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {translateInterfaceText("Voltar para Extrato Administrativo")}</button>

            {/* Bloco 1 - Informações do Pagamento */}
            <Card className="p-6 rounded-2xl border border-slate-200 shadow-none bg-white">
              <div className="mb-5">
                <h2 className="text-[15px] font-semibold text-[#0A1A2F]">
                  {translateInterfaceText("Informações do Pagamento")}</h2>
                <p className="text-[12.5px] text-[#6B7280] mt-0.5">
                  {translateInterfaceText("Detalhes da solicitação enviada pelo afiliado.")}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <MiniCard icon={Mail} label={translateInterfaceText("Email do usuário")} value={payment?.email || '—'} />
                <MiniCard icon={Hash} label={translateInterfaceText("ID do payout")} value={payment?.id_payout || '—'} />
                <MiniCard
                  icon={Wallet}
                  label={translateInterfaceText("Valor total")}
                  value={formatBRL(payment?.totalAmount)}
                />
                <MiniCard
                  icon={TrendingUp}
                  label={translateInterfaceText("Valor líquido")}
                  value={formatBRL(payment?.amount)}
                />
                <MiniCard icon={Receipt} label={translateInterfaceText("Taxas")} value={formatBRL(payment?.totalTaxe)} />
                <MiniCard
                  icon={Activity}
                  label={translateInterfaceText("Status atual")}
                  value={
                    <Badge
                      variant="outline"
                      className={cn(
                        'rounded-full px-2.5 py-0.5 font-medium gap-1.5',
                        statusStyles[currentStatusKey].cls,
                      )}
                    >
                      <span
                        className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          statusStyles[currentStatusKey].dot,
                        )}
                      />
                      {statusStyles[currentStatusKey].label}
                    </Badge>
                  }
                />
              </div>
            </Card>

            {/* Bloco 2 - Atualização (60%) | Comprovante (40%) */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              {/* Esquerda - Atualização */}
              <Card className="lg:col-span-3 p-6 md:p-7 rounded-2xl border border-slate-200 shadow-[0_1px_2px_rgba(16,24,40,0.04)] bg-white">
                <div className="mb-6">
                  <h2 className="text-[15px] font-semibold text-[#0A1A2F]">
                    {translateInterfaceText("Atualização do Pagamento")}</h2>
                  <p className="text-[12.5px] text-[#6B7280] mt-0.5">
                    {translateInterfaceText("Gerencie o status da solicitação de saque.")}</p>
                </div>

                <div className="space-y-5">
                  {/* Status */}
                  <div className="space-y-2">
                    <Label className="text-[13px] font-medium text-[#374151]">{translateInterfaceText("Novo status")}</Label>
                    <Select
                      value={newStatus}
                      onValueChange={(v) => setNewStatus(v as PayoutStatus)}
                    >
                      <SelectTrigger className="h-11 w-full max-w-[450px] rounded-xl border-slate-200 text-[13px] bg-white shadow-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="requested">{translateInterfaceText("Solicitado")}</SelectItem>
                        <SelectItem value="pending">{translateInterfaceText("Pendente")}</SelectItem>
                        <SelectItem value="paid">{translateInterfaceText("Pago")}</SelectItem>
                        <SelectItem value="failed">{translateInterfaceText("Falhou")}</SelectItem>
                        <SelectItem value="canceled">{translateInterfaceText("Cancelado")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Observação interna */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[13px] font-medium text-[#374151]">
                        {translateInterfaceText("Observação interna")}{' '}
                        <span className="text-[#9CA3AF] font-normal">{translateInterfaceText("(opcional)")}</span>
                      </Label>
                      <span className="text-[11px] text-[#9CA3AF]">{note.length}/500</span>
                    </div>
                    <Textarea
                      onChange={(e) => setNote(e.target.value.slice(0, 500))}
                      placeholder={translateInterfaceText("Adicione observações internas sobre este pagamento.")}
                      className="min-h-[96px] rounded-xl border-slate-200 text-[13px] resize-none shadow-none focus-visible:ring-1 focus-visible:ring-[#2563EB]"
                    >{note}</Textarea>
                  </div>
                </div>
              </Card>

              {/* Direita - Comprovante */}
              <Card className="lg:col-span-2 p-6 md:p-7 rounded-2xl border border-slate-200 shadow-[0_1px_2px_rgba(16,24,40,0.04)] bg-white">
                <div className="mb-6">
                  <h2 className="text-[15px] font-semibold text-[#0A1A2F]">
                    {translateInterfaceText("Comprovante de Transferência")}</h2>
                  <p className="text-[12.5px] text-[#6B7280] mt-0.5">
                    {translateInterfaceText("Envie ou consulte o comprovante deste pagamento.")}</p>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFile(file);
                  }}
                />

                {hasReceipt ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                      <div className="w-12 h-12 rounded-lg bg-white border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                        {receiptSrc ? (
                          <img
                            src={receiptSrc}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <FileText className="w-5 h-5 text-[#2563EB]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-[#0A1A2F] truncate">
                          {proofFile?.name || 'comprovante.png'}
                        </p>
                        <p className="text-[11.5px] text-[#6B7280]">
                          {proofFile ? translateInterfaceText("Pronto para envio") : translateInterfaceText("Comprovante atual")}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsReceiptOpen(true)}
                        className="flex-1 h-10 rounded-xl border-[#E5EAF3] text-[#374151] bg-white hover:bg-white hover:text-[#374151] hover:border-[#E5EAF3] gap-1.5 text-[12.5px] font-medium transition-colors duration-150"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        {translateInterfaceText("Ver comprovante")}</Button>
                      <Button
                        variant="outline"
                        onClick={() => inputRef.current?.click()}
                        className="flex-1 h-10 rounded-xl border-[#E5EAF3] text-[#374151] bg-white hover:bg-white hover:text-[#374151] hover:border-[#E5EAF3] gap-1.5 text-[12.5px] font-medium transition-colors duration-150"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        {translateInterfaceText("Substituir")}</Button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={cn(
                      'rounded-xl border border-dashed p-6 text-center cursor-pointer transition-colors',
                      dragOver
                        ? 'border-[#2563EB] bg-[#EEF2FF]/60'
                        : 'border-[#DCE5FF] bg-[#FAFBFF] hover:bg-[#EEF2FF]/40',
                    )}
                  >
                    <div className="flex flex-col items-center gap-2 py-2">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <UploadIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <p className="text-[13px] font-medium text-[#0A1A2F]">
                        {translateInterfaceText("Nenhum comprovante enviado")}</p>
                      <p className="text-[11.5px] text-[#6B7280]">{translateInterfaceText("PNG ou JPG · até 10MB")}</p>
                      <Button
                        type="button"
                        className="mt-3 h-9 px-4 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[12.5px] font-medium gap-1.5"
                      >
                        <UploadIcon className="w-3.5 h-3.5" />
                        {translateInterfaceText("Enviar comprovante")}</Button>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* CTAs */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                disabled={submitting}
                className="w-full sm:w-auto sm:min-w-[120px] h-11 rounded-xl border-[#E5EAF3] text-[#374151] bg-white hover:bg-white hover:text-[#374151] hover:border-[#E5EAF3] text-[13px] font-medium transition-colors duration-150"
              >
                {translateInterfaceText("Cancelar")}</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full sm:w-[240px] h-11 rounded-xl bg-[#2563EB] hover:bg-[#2563EB] text-white shadow-sm text-[13.5px] font-semibold transition-colors duration-150 gap-2"
              >
                {submitting ? (
                  <>
                    <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    {translateInterfaceText("Processando...")}</>
                ) : (
                  translateInterfaceText("Atualizar Pagamento")
                )}
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* Modal Visualização de Comprovante */}
      <Dialog open={isReceiptOpen} onOpenChange={setIsReceiptOpen}>
        <DialogContent className="w-[760px] max-w-[95vw] rounded-2xl p-0 overflow-hidden gap-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <div>
              <DialogTitle className="text-[18px] font-bold text-[#111827]">
                {translateInterfaceText("Comprovante de Pagamento")}</DialogTitle>
              <p className="text-[12.5px] text-[#6B7280] mt-1">
                {formatBRL(payment?.amount)} ·{' '}
                <span className="text-[#2563EB] font-medium">
                  {statusStyles[newStatus].label}
                </span>
              </p>
            </div>
          </DialogHeader>

          <div className="h-px bg-[#EEF0F3]" />

          <div className="p-6 bg-slate-50">
            <div
              className="flex items-center justify-center cursor-zoom-in"
              onClick={() => receiptSrc && setZoom(true)}
            >
              <div className="relative max-h-[60vh] w-full flex items-center justify-center bg-white rounded-xl border border-slate-200 overflow-hidden p-4">
                {receiptSrc ? (
                  <img
                    src={receiptSrc}
                    alt={translateInterfaceText("Comprovante")}
                    className="max-h-[55vh] object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        'https://placehold.co/400x300?text=Erro+ao+carregar+imagem';
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-center py-20">
                    <Receipt className="w-10 h-10 text-[#9CA3AF] mb-2" />
                    <p className="text-[13px] text-[#6B7280]">{translateInterfaceText("Nenhuma imagem disponível")}</p>
                  </div>
                )}
                {receiptSrc && (
                  <div className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/90 border border-slate-200 flex items-center justify-center text-[#2563EB]">
                    <ZoomIn className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="h-px bg-[#EEF0F3]" />

          <div className="px-6 py-4 flex flex-col sm:flex-row sm:justify-end gap-2">
            <Button
              variant="outline"
              disabled={!receiptSrc}
              onClick={() => receiptSrc && window.open(receiptSrc, '_blank')}
              className="h-10 rounded-xl border-[#E5EAF3] text-[#374151] bg-white hover:bg-white hover:text-[#374151] hover:border-[#E5EAF3] gap-2 transition-colors duration-150"
            >
              <ExternalLink className="w-4 h-4" />
              {translateInterfaceText("Abrir em nova guia")}</Button>
            <Button
              variant="outline"
              disabled={!receiptSrc}
              onClick={() => {
                if (!receiptSrc) return;
                const a = document.createElement('a');
                a.href = receiptSrc;
                a.download = proofFile?.name || 'comprovante.png';
                a.click();
              }}
              className="h-10 rounded-xl border-[#E5EAF3] text-[#374151] bg-white hover:bg-white hover:text-[#374151] hover:border-[#E5EAF3] gap-2 transition-colors duration-150"
            >
              <Download className="w-4 h-4" />
              {translateInterfaceText("Baixar comprovante")}</Button>
            <Button
              onClick={() => setIsReceiptOpen(false)}
              className="h-10 rounded-xl bg-[#2563EB] hover:bg-[#2563EB] text-white transition-colors duration-150"
            >
              {translateInterfaceText("Fechar")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Zoom Modal */}
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="w-[95vw] max-w-[1100px] h-[90vh] rounded-2xl p-0 overflow-hidden bg-black/95 border-0">
          <button
            onClick={() => setZoom(false)}
            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-full h-full flex items-center justify-center p-6">
            {receiptSrc && (
              <img
                src={receiptSrc}
                alt={translateInterfaceText("Comprovante ampliado")}
                className="max-h-full max-w-full object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
